/* eslint-disable sort-keys */
/* eslint-disable max-lines */
import {
    ChatAltIcon,
    EmojiHappyIcon,
    ExclamationCircleIcon,
    NinchatEmbedSendIcon,
} from '../Icon'
import {
    Dropdown,
    DropdownButtonNew as DropdownButton,
    DropdownContent,
} from '../Dropdown'
import {
    FileProps,
    MessageState,
    SendRefType,
    SendViewProps,
    SendViewStateProps,
} from './types'
import React, {
    Ref, useContext, useRef, useState,
} from 'react'


import {
    clearFileUpload,
    clearMessageState,
} from '../../state/ui'

import {
    form,
    messageBlocked,
    pickerWrapper,
    sendIcon,
    sendViewWrapper,
    silencedMessages,
    textarea,
} from './styles/SendView'

import {
    getCannedMessageText, getCannedMessagesKey,
} from 'utils/cannedmessages'
import {
    useAppSelector, useAppSelectorWithShallowEquality,
} from 'hooks'

import Attachment from '../Attachment'
import Button from '../Button'

import {CannedMessageProps} from '../SendCannedMessages/types'
import {DEFAULT_HEADER_HEIGHT} from 'components/Embed/components/EmbedHeader/EmbedHeader'

import {DropdownState} from 'components/Dropdown/types'
import {EmbedContext} from 'components/EmbedContext'
import {Icon} from 'components/Icon/types'
import {JitsiState} from 'reducers/jitsi'

import {NinchatCannedMessage} from 'api/types/properties'
import {NinchatUserSettings} from 'api/types/settings'
import Picker from '@emoji-mart/react'
import SendCannedMessages from '../SendCannedMessages'
import SendFiles from '../SendFiles'
import SendNotes from '../SendNotes'
import SendStatus from '../SendStatus'
import SendTags from '../SendTags'
import data from '@emoji-mart/data'
import {fileUtil} from '../../utils'
import {styled} from '../../stitches/config'
import {styledItem} from 'components/Dropdown/styles'

import {useDispatch} from 'react-redux'
import {useTranslation} from 'react-i18next'
import {useUiState} from 'hooks/state'

const StyledActionFieldset = styled('fieldset', {
    alignItems: 'center',
    border: 0,
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-end',
    margin: 0,
    variants: {
        isEmbedQueueChannel: {
            true: {
                'button:not([data-id="send-button"])': {
                    'padding': '$buttonCommandBar',
                    'margin': '$buttonCommandBarMargin',
                    'borderRadius': '$buttonCommandBar',
                    'borderStyle': '$buttonCommandBar',
                    'borderWidth': '$buttonCommandBar',
                    'borderColor': '$buttonCommandBarBorder',
                    '&:disabled': {borderColor: '$buttonCommandBarBorderDisabled'},
                    '&:not(:disabled)': {
                        '&:active, &:hover:active': {borderColor: '$buttonCommandBarBorderActive'},
                        '&:focus-visible': {borderColor: '$buttonCommandBarBorderFocus'},
                        '&:hover': {borderColor: '$buttonCommandBarBorderHover'},
                    },
                },
            },
        },
    },
})

const StyledActionsLeft = styled('div', {
    alignItems: 'center',
    display: 'flex',
    flexDirection: 'row',
    flexGrow: 1,
})

const StyledItem = styled('div', styledItem)
const StyledPickerWrapper = styled('div', pickerWrapper)
const StyledSilencedMessage = styled('div', silencedMessages)

const StyledForm = styled('form', form)
const StyledTextarea = styled('textarea', textarea)
const StyledSendViewWrapper = styled('div', sendViewWrapper)

const StyledIcon = styled('div', sendIcon)

const StyledMessageBlocked = styled('div', messageBlocked)

const SAME_MESSAGE_TIME_FRAME = 30
const HIDE_SAME_MESSAGE_ERROR = 3

// eslint-disable-next-line complexity
const SendView = React.forwardRef((props: SendViewProps, ref: Ref<SendRefType>): React.ReactElement => {
    const dispatch = useDispatch()
    const embed = useContext(EmbedContext)
    const isEmbedQueueChannel = embed?.variant === 'queue' || embed?.variant === 'channel'
    const isEmbedContainer = !embed?.floating
    const isEmbedContainerWithHeader = isEmbedContainer && embed?.containerOptions?.showHeader

    const uiState = useUiState()

    const {
        cannedMessages,
        supportedCannedMessagesLanguages,
        chat,
        chat_id,
        closed,
        features,
        handleSend,
        showAttachment,
        isAudienceChannel,
        isVideoChannel,
        isSilenced,
        memberMessageMetadata,
        messageState,
        onSaveNotes,
        onSaveTags,
        realm_id,
        setMessageState,
        pastMessages,
        suspended,
        tags,
    } = props

    const _tags = JSON.parse(JSON.stringify(tags))

    const defaultSendViewState: SendViewStateProps = {
        cannedMessagesOpen: false,
        notesOpen: false,
        tagsOpen: false,
    }

    const {activeVideoChannelId} = useAppSelectorWithShallowEquality(({jitsi}: {jitsi: JitsiState}) => ({activeVideoChannelId: jitsi.activeVideoChannelId}))
    const isActiveVideoChannel = activeVideoChannelId === chat.channel_id

    const [sendViewState, setSendViewState] = React.useState(defaultSendViewState)
    const [multilineEnabled, setMultilineEnabled] = React.useState(false)
    const textareaInitialHeight = useRef(0)
    const [showMessageBlocked, setShowMessageBlocked] = useState(false)

    const {t} = useTranslation()

    const cannedMessageKey = useAppSelector(({ui}) => getCannedMessagesKey(supportedCannedMessagesLanguages, ui?.canned_message_key))
    const sendTextArea = React.useRef<HTMLTextAreaElement>(null)

    const emojiPickerTranslations = {
        categories: {
            activity: t('Activity'),
            custom: t('Custom'),
            flags: t('Flags'),
            foods: t('Food & Drink'),
            frequent: t('Frequently used'),
            nature: t('Animals & Nature'),
            objects: t('Objects'),
            people: t('Smileys & People'),
            places: t('Travel & Places'),
            symbols: t('Symbols'),
        },
        skins: {
            1: t('Default'),
            2: t('Light'),
            3: t('Medium-Light'),
            4: t('Medium'),
            5: t('Medium-Dark'),
            6: t('Dark'),
            choose: t('Choose default skin tone'),
        },
    }

    const onSend = (): void => {
        if (isEmbedQueueChannel) {
            const now = new Date().getTime()
            // eslint-disable-next-line no-extra-parens
            const ownMessagesWithinTimeFrame = pastMessages?.filter((message) => now - new Date(message.time).getTime() < SAME_MESSAGE_TIME_FRAME * 1000)

            if (ownMessagesWithinTimeFrame?.find((message) => message.text === messageState.message)) {
                setShowMessageBlocked(true)
                setTimeout(() => setShowMessageBlocked(false), HIDE_SAME_MESSAGE_ERROR * 1000)
                return
            }
        }

        setShowMessageBlocked(false)
        if (messageState.message.indexOf('/') === 0) {
            return
        }

        dispatch(clearMessageState(messageState.chat))
        setMultilineEnabled(false)
        handleSend()
    }

    const handleFormSubmit = (event: React.FormEvent<HTMLFormElement>): void => {
        event.preventDefault()
        onSend()
    }

    const onChange = (event: React.ChangeEvent<HTMLTextAreaElement>): void => {
        const newSendViewState: SendViewStateProps = {...sendViewState}

        if (event.target.value.indexOf('/') === 0) {
            newSendViewState.cannedMessagesOpen = true
            newSendViewState.cannedMessagesFilter = event.target.value
            newSendViewState.tagsOpen = false
        } else if (newSendViewState.cannedMessagesOpen) {
            newSendViewState.cannedMessagesOpen = false
            delete newSendViewState.cannedMessagesFilter
        }

        if (event.target.value.indexOf('#') === 0 && isAudienceChannel) {
            newSendViewState.cannedMessagesOpen = false
            newSendViewState.tagsOpen = true
        } else if (newSendViewState.tagsOpen) {
            newSendViewState.tagsOpen = false
        }

        setSendViewState(newSendViewState)
        setMessageState({
            ...messageState,
            message: event.target.value,
        })
    }

    const onFiles = (files: FileList): void => {
        const fileArray: Array<FileProps> = []
        Array.from(files).forEach((file: FileProps): void => {
            if (fileUtil.isImage(file.type)) {
                file.thumbnail = URL.createObjectURL(file)
            }
            fileArray.push(file)
        })
        if (fileArray.length) {
            const newMessageState: MessageState = {...messageState}
            const newFiles = fileArray.filter((file) => newMessageState.files.find((f) => f.name === file.name) === undefined)
            newMessageState.files = [...newMessageState.files, ...newFiles]
            setMessageState(newMessageState)
            sendTextArea.current?.focus()
        }
    }

    const onRemoveFile = (filename: string): void => {
        const newMessageState: MessageState = {...messageState}
        if (newMessageState.files.length) {
            const fileArray = newMessageState.files.filter((file: FileProps): boolean => {
                if (file.name === filename) {
                    if (file.error) {
                        dispatch(clearFileUpload({
                            chat_id,
                            filename,
                        }))
                    }
                    return false
                }

                return true
            })
            newMessageState.files = fileArray
        }
        setMessageState(newMessageState)
    }

    const states = [
        'cannedMessagesOpen',
        'notesOpen',
        'tagsOpen',
    ] as const

    type ViewState = typeof states[number]

    const onShowMessage = (): void => {
        setSendViewState({
            ...sendViewState,
            notesOpen: false,
            tagsOpen: false,
        })
    }

    const onToggleViewState = (view: ViewState, forceClose?: boolean): void => {
        const newSendViewState: SendViewStateProps = {...sendViewState}

        const picked = ((
            {
                cannedMessagesOpen,
                notesOpen,
                tagsOpen,
            },
        ) => (
            {
                cannedMessagesOpen,
                notesOpen,
                tagsOpen,
            }
        ))(newSendViewState)

        // eslint-disable-next-line init-declarations
        let k: keyof typeof picked

        for (k in picked) {
            if (k === view) {
                if (forceClose) {
                    // Don't toggle but close
                    picked[k] = false
                } else {
                    picked[k] = !picked[k]
                }

                if (picked[k] === false) {
                    // Something was closed so let's focus textarea
                    sendTextArea.current?.focus()
                }
            } else {
                picked[k] = false
            }
        }

        delete newSendViewState.cannedMessagesFilter

        setSendViewState({
            ...newSendViewState,
            ...picked,
        })
    }

    const onSelectCannedMessage = (m: CannedMessageProps): void => {
        if (closed || suspended) {
            return
        }

        const cursor = sendTextArea?.current?.selectionStart ?? 0
        const currentKey = m?.withLegacyKey
            ? getCannedMessagesKey(supportedCannedMessagesLanguages, 'message')
            : cannedMessageKey
        const cannedMessageText = getCannedMessageText(m, currentKey)

        if (messageState.message.indexOf('/') === 0) {
            // Replace the entire message when using / shortcut
            setMessageState({
                ...messageState,
                message: cannedMessageText,
            })
        } else {
            // Add canned message to the caret position in other cases
            setMessageState({
                ...messageState,
                message: `${messageState.message.substring(0, cursor)}${cannedMessageText}${messageState.message.substring(cursor)}`,
            })

            setTimeout(() => {
                if (sendTextArea?.current) {
                    sendTextArea.current.selectionStart = cursor + cannedMessageText.length
                }
            }, 0)
        }

        onToggleViewState('cannedMessagesOpen', true)
    }

    const onHandleKeyword = (): void => {
        const keyword = messageState.message.substring(1, messageState.message.length)
        const selectedCannedMsg = cannedMessages?.find((item) => item.keywords[0].toLowerCase() === keyword.toLowerCase())
        if (selectedCannedMsg && getCannedMessageText(selectedCannedMsg, cannedMessageKey)) {
            onSelectCannedMessage(selectedCannedMsg)
        }
    }

    const onKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>): void => {
        if (!event.shiftKey) {
            switch (event.key) {
                case ' ':
                    if (messageState.message.indexOf('/') === 0) {
                        // Message field possibly contains canned message key
                        onHandleKeyword()
                        event.preventDefault()
                    }
                    break
                case 'Enter':
                    event.preventDefault()

                    if (messageState.message.indexOf('/') === 0) {
                        // Message field possibly contains canned message key
                        onHandleKeyword()
                    } else {
                        onSend()
                    }
                    break
                default:
            }
        } else if (event.shiftKey) {
            switch (event.key) {
                case 'Enter':
                    setMultilineEnabled(true)
                    break
                default:
            }
        }
    }

    const [emojiDropdownOpen, setEmojiDropdownOpen] = useState(false)

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const onSelectEmoji = (emoji: any): void => {
        const cursor = sendTextArea?.current?.selectionStart ?? 0
        setMessageState({
            ...messageState,
            message: `${messageState.message.substring(0, cursor)}${emoji.native}${messageState.message.substring(cursor)}`,
        })
        setTimeout(() => {
            if (sendTextArea?.current) {
                sendTextArea.current.selectionStart = cursor + emoji.native.length
            }
        }, 0)
        setEmojiDropdownOpen(false)
    }

    const handleDropdownChange = (isOpen: DropdownState) => {
        setEmojiDropdownOpen(isOpen)
    }

    React.useImperativeHandle(ref, () => ({
        selectCannedMessage: (m: NinchatCannedMessage) => {
            onSelectCannedMessage(m)
        },
    }))

    React.useEffect(() => {
        if (!isAudienceChannel) {
            setSendViewState({
                ...sendViewState,
                notesOpen: false,
                tagsOpen: false,
            })
        }

        if (!(isEmbedQueueChannel && isEmbedContainer)) {
            sendTextArea.current?.focus()
        }
    }, [chat_id])

    React.useEffect(() => {
        if (sendTextArea?.current) {
            embed?.setEmbedState((prevState) => ({
                ...prevState,
                chatTextareaRef: sendTextArea,
            }))
        }
    }, [sendTextArea?.current])

    React.useLayoutEffect(() => {
        textareaInitialHeight.current = sendTextArea?.current?.clientHeight || 0
    }, [])

    React.useEffect(() => {
        if (isEmbedQueueChannel) {
            if (
                sendTextArea.current &&
            textareaInitialHeight.current < sendTextArea.current.clientHeight
            ) {
                setMultilineEnabled(true)
            }

            if (!messageState.message) {
                setMultilineEnabled(false)
            }
        }
    }, [messageState.message, sendTextArea.current, isEmbedQueueChannel])

    const placeholder = closed ? t('Conversation has ended.') : isEmbedQueueChannel ? t('Enter message') : t('Shift + Enter to add new line. Enter to send.')
    const currentTheme = (uiState?.theme || 'light') as NinchatUserSettings['theme']

    const embedSendIcon = () => {
        if (embed?.sendIcon && Object.entries(embed?.sendIcon).length === 0) {
            return null
        } else if (embed?.sendIcon?.svg) {
            // eslint-disable-next-line react/no-danger
            return <StyledIcon aria-hidden='true' dangerouslySetInnerHTML={{__html: embed.sendIcon.svg}} />
        } else if (embed?.sendIcon?.src) {
            return <img aria-hidden='true' src={embed.sendIcon.src} />
        }
        return <NinchatEmbedSendIcon color='white' />
    }

    return (
        <StyledSendViewWrapper
            css={
                {$$headerHeight: `${embed?.containerOptions?.headerOptions?.height || DEFAULT_HEADER_HEIGHT}px`}
            }
            isActiveVideoChannel={isActiveVideoChannel}
            isEmbedContainerWithHeader={isEmbedContainerWithHeader}
            isEmbedQueueChannel={isEmbedQueueChannel}
            isVideoChannel={isVideoChannel}
        >
            {showMessageBlocked ? <StyledMessageBlocked><ExclamationCircleIcon />{t('You cannot send the same message twice')}</StyledMessageBlocked> : null}
            {!!(features?.notes || features?.tags) && !isEmbedQueueChannel &&
                <div className='tabContainer'>
                    <div className='rowParent tabs'>
                        <div className='flexChild rowParent'>
                            <button
                                className={`tab ${!sendViewState.tagsOpen && !sendViewState.notesOpen ? 'selected' : ''}`}
                                onClick={(): void => onShowMessage()}
                                type='button'
                            >
                                {t('Message')}
                            </button>
                            {!!features?.notes &&
                                <button
                                    className={`tab ${sendViewState.notesOpen ? 'selected' : ''}`}
                                    disabled={suspended}
                                    onClick={(): void => onToggleViewState('notesOpen')}
                                    title={t('Add notes')}
                                    type='button'
                                >
                                    {t('Notes')}
                                </button>}
                            {!!features?.tags &&
                                <button
                                    className={`tab ${sendViewState.tagsOpen ? 'selected' : ''}`}
                                    onClick={(): void => onToggleViewState('tagsOpen')}
                                    title={t('Tags')}
                                    type='button'
                                >
                                    {t('Tags')}
                                    {Boolean((memberMessageMetadata?.tag_ids || []).length) &&
                                        <span className='count'><span>{(memberMessageMetadata?.tag_ids || []).length}</span></span>}
                                </button>}
                        </div>
                        {Boolean((memberMessageMetadata?.tag_ids || []).length) &&
                            <div className='flexChild columnParent status'>
                                <SendStatus tagIds={memberMessageMetadata?.tag_ids || []} tags={_tags} />
                            </div>}
                    </div>
                </div>}
            <div className={`flexChild cannedMessages ${sendViewState.cannedMessagesOpen ? 'open' : ''}`}>
                {!!sendViewState.cannedMessagesOpen &&
                    <SendCannedMessages
                        cannedMessages={cannedMessages || []}
                        filter={sendViewState.cannedMessagesFilter}
                        onClose={(): void => onToggleViewState('cannedMessagesOpen', true)}
                        onSelect={onSelectCannedMessage}
                        realmId={realm_id}
                        supportedCannedMessagesLanguages={supportedCannedMessagesLanguages || []}
                    />}
            </div>
            <div className={`flexChild notes ${sendViewState.notesOpen ? 'open' : ''}`}>
                {!!sendViewState.notesOpen &&
                    <SendNotes onSaveNotes={onSaveNotes} savedNotes={memberMessageMetadata?.Notes || []} {...{chat}} />}
            </div>
            <div className={`flexChild tags ${sendViewState.tagsOpen ? 'open' : ''}`}>
                {!!sendViewState.tagsOpen &&
                    <SendTags
                        disabled={suspended}
                        isVideoChannel={isVideoChannel}
                        onClose={(): void => onToggleViewState('tagsOpen', true)}
                        onSaveTags={onSaveTags}
                        tagIds={memberMessageMetadata?.tag_ids || []}
                        tags={_tags}
                    />}
            </div>
            {messageState.files.length > 0 &&
                <SendFiles files={messageState.files} onRemove={onRemoveFile} />}
            {!sendViewState.notesOpen && !sendViewState.tagsOpen && !isSilenced &&
                <StyledForm isEmbedQueueChannel={isEmbedQueueChannel} multilineEnabled={multilineEnabled} onSubmit={handleFormSubmit}>
                    <fieldset className='sendWrapper' >
                        <div
                            className='growWrap'
                            data-replicated-value={
                                `${messageState.message} `
                            }
                        >
                            <StyledTextarea
                                aria-label={t('Enter message')}
                                data-id='send-message-textarea'
                                disabled={closed || suspended}
                                isEmbedQueueChannel={isEmbedQueueChannel}
                                onChange={onChange}
                                onKeyDown={onKeyDown}
                                placeholder={placeholder}
                                ref={sendTextArea}
                                rows={1}
                                value={messageState.message}
                            />
                        </div>
                    </fieldset>
                    <StyledActionFieldset isEmbedQueueChannel={isEmbedQueueChannel}>
                        <StyledActionsLeft >
                            {!!features?.cannedMessages && !isEmbedQueueChannel &&
                                <Button
                                    color='text'
                                    disabled={closed || suspended}
                                    iconLeft={ChatAltIcon}
                                    onPress={(): void => onToggleViewState('cannedMessagesOpen')}
                                    title={t('Canned messages')}
                                />}
                        </StyledActionsLeft>
                        <Dropdown closeAfterSelection={false} forceOpenState={emojiDropdownOpen} onStateChange={handleDropdownChange}>
                            <DropdownButton
                                caret={false}
                                color='text'
                                disabled={closed || suspended}
                                iconLeft={EmojiHappyIcon}
                                title={t('Add Emoji')}
                            />
                            <DropdownContent customClass='unstyled' direction='top-right'>
                                <StyledItem>
                                    <StyledPickerWrapper theme={currentTheme}>
                                        <Picker
                                            data={data}
                                            i18n={emojiPickerTranslations}
                                            onEmojiSelect={onSelectEmoji}
                                            perLine={8}
                                            previewPosition='none'
                                            searchPosition='none'
                                            set='native'
                                            theme={currentTheme}
                                        />
                                    </StyledPickerWrapper>
                                </StyledItem>
                            </DropdownContent>
                        </Dropdown>
                        {showAttachment ? <Attachment disabled={closed || suspended} onFiles={onFiles} /> : null}
                        {
                            isEmbedQueueChannel ?
                                <Button
                                    color='filled'
                                    css={{
                                        '$colors-buttonFilledBackground': '$colors-buttonSendBackground',
                                        '$colors-buttonFilledBackgroundActive': '$colors-buttonSendBackgroundActive',
                                        '$colors-buttonFilledBackgroundDisabled': '$colors-buttonSendBackgroundDisabled',
                                        '$colors-buttonFilledBackgroundFocus': '$colors-buttonSendBackgroundFocus',
                                        '$colors-buttonFilledBackgroundHover': '$colors-buttonSendBackgroundHover',
                                        '$colors-buttonFilledBorder': '$colors-buttonSendBorder',
                                        '$colors-buttonFilledBorderActive': '$colors-buttonSendBorderActive',
                                        '$colors-buttonFilledBorderDisabled': '$colors-buttonSendBorderDisabled',
                                        '$colors-buttonFilledBorderFocus': '$colors-buttonSendBorderFocus',
                                        '$colors-buttonFilledBorderHover': '$colors-buttonSendBorderHover',
                                        '$colors-buttonFilledIcon': '$colors-buttonSendIcon',
                                        '$colors-buttonFilledIconDisabled': '$colors-buttonSendIconDisabled',
                                        '$colors-buttonFilledIconFocus': '$colors-buttonSendIconFocus',
                                        '$colors-buttonFilledIconActive': '$colors-buttonSendIconActive',
                                        '$colors-buttonFilledIconHover': '$colors-buttonSendIconHover',
                                        '$colors-buttonFilledText': '$colors-buttonSendText',
                                        '$colors-buttonFilledTextDisabled': '$colors-buttonSendTextDisabled',
                                        '$colors-buttonFilledTextActive': '$colors-buttonSendTextActive',
                                        '$colors-buttonFilledTextFocus': '$colors-buttonSendTextFocus',
                                        '$colors-buttonFilledTextHover': '$colors-buttonSendTextHover',
                                        '$colors-buttonFilledOutlineFocus': '$colors-buttonSendOutlineFocus',
                                        'borderRadius': '$buttonSend',
                                        'borderStyle': '$buttonSend',
                                        'borderWidth': '$buttonSend',
                                        'fontFamily': '$buttonSend',
                                        'fontSize': '$buttonSend',
                                        'fontStyle': '$buttonSend',
                                        'fontVariant': '$buttonSend',
                                        'fontWeight': '$buttonSend',
                                        'lineHeight': '$buttonSend',
                                        'padding': '$buttonSend',
                                        'margin': '$buttonSendMargin',
                                    }}
                                    data-id='send-button'
                                    disabled={closed || suspended}
                                    iconRight={embedSendIcon as unknown as Icon}
                                    text={embed?.sendText}
                                    title={embed?.sendText}
                                    type='submit'
                                /> :
                                <Button
                                    disabled={closed || suspended}
                                    text={t('Send')}
                                    title={t('Send message')}
                                    type='submit'
                                />
                        }
                    </StyledActionFieldset>
                </StyledForm>}
            {isSilenced ? <StyledSilencedMessage><ExclamationCircleIcon />{t('You can\'t enter message to this channel')}</StyledSilencedMessage> : null}
        </StyledSendViewWrapper>
    )
})

SendView.displayName = 'SendView'

export default SendView
